import * as Sentry from '@sentry/browser';
import { CONTENT_TYPES, ENTITY_HEADERS } from './constants';
import { histogram } from './dataDogUtils';
import getSessionAndTrackingIdsFromCookies from './getSessionAndTrackingIdsFromCookies';

export const API_HOST =
    process.env.API_HOST || process.env.STORYBOOK_API_HOST || '';

const defaultCredentials = 'include';

const defaultHeaders = (application) => {
    const { sessionId, trackingId } = getSessionAndTrackingIdsFromCookies();
    try {
        return {
            Accept: 'application/json',
            [ENTITY_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
            'ZD-Application-Name': `${application}`,
            'ZD-Application-Version': window.ZD?.configs?.RELEASE || '',
            'ZD-Client-ServiceName': 'monolith',
            'ZD-Referer': document.referrer,
            'ZD-Url': window.location.href,
            'ZD-User-Agent': window.navigator.userAgent,
            'ZD-Tracking-Id': trackingId,
            'ZD-Session-Id': sessionId,
        };
    } catch (err) {
        return {
            Accept: 'application/json',
            [ENTITY_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
            'ZD-Application-Name': `${application}`,
            'ZD-Application-Version': '',
            'ZD-Client-ServiceName': 'monolith',
            'ZD-Referer': '',
            'ZD-Url': '',
            'ZD-User-Agent': '',
            'ZD-Tracking-Id': trackingId,
            'ZD-Session-Id': sessionId,
        };
    }
};

/**
 * fetchWithDatadog
 * @param {String} application
 * @param {String} clientService
 * @param {String} credentials - default value 'include'
 * @param {String} endpoint
 * @param {Object} headerOverrides - includes any additional or overridden headers on top of the default
 * @param {HTTP_METHODS} method - HTTP request type: 'DELETE', 'GET', 'POST', or 'PUT'
 * @param {String} operationId - underscore-delimited datadog metric name, e.g. appointments_get
 * @param {String} pageName - underscore-delimited page name for datadog metrics, e.g. appointment_details
 * @param {Object} extras - any other fields that should be added to fetch, e.g. body
 * @returns {Promise<Response>}
 */
export async function fetchWithDatadog({
    application = null,
    clientService = null,
    credentials = defaultCredentials,
    endpoint,
    headerOverrides = {},
    method,
    operationId,
    pageName,
    ...extras
}) {
    let response = null;
    let endTime;
    let tags = [];
    const startTime = Date.now();
    const applicationName = application || clientService || 'frontend-common';
    const clientServiceName = clientService || application || 'frontend-common';

    const headers = {
        ...defaultHeaders(applicationName),
        ...headerOverrides,
    };

    const targetService = headers['ZD-Client-ServiceName'];

    try {
        response = await fetch(endpoint, {
            credentials,
            headers,
            method,
            ...extras,
        });

        endTime = Date.now();
        const status = response?.status;
        tags = datadogTags({
            application: applicationName,
            clientService: clientServiceName,
            status: 'ok',
            operationId,
            pageName,
            statusCode: status,
            targetService,
        });
    } catch (error) {
        endTime = Date.now();
        tags = datadogTags({
            application: applicationName,
            clientService: clientServiceName,
            status: 'error',
            operationId,
            pageName,
            statusCode: error.status,
            targetService,
        });

        Sentry.captureException(error);
    }
    const durationMs = endTime - startTime;

    histogram({
        name: 'http_client.requests.duration_ms',
        value: durationMs,
        tags,
    });

    return response;
}

const datadogTags = ({
    application,
    clientService,
    status,
    operationId,
    pageName,
    statusCode,
    targetService,
}) => [
    `application:${application}`,
    `client_service:${clientService}`,
    `target_service:${targetService}`,
    `status:${status}`,
    `status_code:${statusCode}`,
    `operation_id:${operationId}`,
    `page_name:${pageName}`,
];
